/**
 * Mutations for Stage Management (rename / reorder / create / delete / automation).
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useJobPipelineMutations } from '@/hooks/useJobOffers';
import type { PipelineStage } from '@/models/job';
import {
  applyActionFeedback,
  feedbackForStageMutation,
} from '@/utils/pipeline-action-feedback';
import { resolveMovedStagePosition } from '@/utils/pipeline-stages';

export function useStageManagementActions(
  jobId: string,
  stages: PipelineStage[]
) {
  const { t } = useTranslation(['job', 'candidates']);
  const { updateStage, createStage, deleteStage } =
    useJobPipelineMutations(jobId);

  const stageIds = useMemo(() => stages.map((stage) => stage.id), [stages]);
  const [renamingStageId, setRenamingStageId] = useState<string | null>(null);

  const handleRename = useCallback(
    (stageId: string, label: string) => {
      setRenamingStageId(stageId);
      void updateStage
        .mutateAsync({ stageId, label })
        .catch((error: unknown) => {
          console.error('Error renaming pipeline stage:', error);
          applyActionFeedback(
            t,
            'candidates:notifications',
            feedbackForStageMutation({
              kind: 'rename',
              error: error instanceof Error ? error.message : String(error),
            })
          );
        })
        .finally(() => setRenamingStageId(null));
    },
    [t, updateStage]
  );

  const handleDelete = useCallback(
    (stageId: string) =>
      deleteStage.mutateAsync(stageId).then(() => undefined, (error: unknown) => {
        console.error('Error deleting pipeline stage:', error);
        applyActionFeedback(
          t,
          'candidates:notifications',
          feedbackForStageMutation({
            kind: 'delete',
            error: error instanceof Error ? error.message : String(error),
          })
        );
      }),
    [deleteStage, t]
  );

  const handleCreate = useCallback(
    async (label: string) => {
      try {
        await createStage.mutateAsync({ label });
        toast.success(
          t('candidates:notifications.stage_create_success', {
            defaultValue: 'Stage created',
            name: label,
          })
        );
      } catch (error: unknown) {
        console.error('Error creating pipeline stage:', error);
        applyActionFeedback(
          t,
          'candidates:notifications',
          feedbackForStageMutation({
            kind: 'create',
            error: error instanceof Error ? error.message : String(error),
          })
        );
        throw error;
      }
    },
    [createStage, t]
  );

  const handleReorder = useCallback(
    (previousOrder: string[], nextOrder: string[]) => {
      const moved = resolveMovedStagePosition(previousOrder, nextOrder);
      if (!moved) return;

      void updateStage
        .mutateAsync({
          stageId: moved.stageId,
          position: moved.position,
        })
        .catch((error: unknown) => {
          console.error('Error reordering pipeline stage:', error);
          applyActionFeedback(
            t,
            'candidates:notifications',
            feedbackForStageMutation({
              kind: 'reorder',
              error: error instanceof Error ? error.message : String(error),
            })
          );
        });
    },
    [t, updateStage]
  );

  const handleAutomationChange = useCallback(
    (stageId: string, enabled: boolean) => {
      void updateStage
        .mutateAsync({ stageId, automationEnabled: enabled })
        .catch((error: unknown) => {
          console.error('Error updating stage automation:', error);
          applyActionFeedback(
            t,
            'candidates:notifications',
            feedbackForStageMutation({
              kind: 'automation',
              error: error instanceof Error ? error.message : String(error),
            })
          );
        });
    },
    [t, updateStage]
  );

  return {
    stageIds,
    renamingStageId,
    isCreating: createStage.isPending,
    isDeleting: deleteStage.isPending,
    handleRename,
    handleDelete,
    handleCreate,
    handleReorder,
    handleAutomationChange,
  };
}
